/*
 * Verweis-Arten des Lesepfads, reine Daten ohne React (Auftrag „Verweis-Chips Optik A“, Betreiber 14.10.2026, r124): EINE
 * Quelle für das Modulwort im Chip und in der Hover-Karte. Darf auch von Server-Code gelesen werden (keine Client-Datei),
 * damit die Marken der Server-Bereinigung und die Ansicht dieselben Werte benutzen.
 */

/**
 * Was ein Verweis im Seiteninhalt sein kann. `intranet` (Beitrag, Event) ist für den künftigen Verweis vorgesehen; `aufgabe`
 * und `prozess` kamen mit der Grunddaten-Sicht (W182, Betreiber W191 A): sie haben noch keinen Verweis im Editor, ihr Chip
 * ist für die Grundsicht bzw. den Verweis auf eine nicht lesbare Aufgabe (Stufe A) vorbereitet.
 */
export type VerweisArt = "cmdb" | "workflow" | "person" | "wiki" | "intranet" | "aufgabe" | "prozess";

/** Das gedämpfte Wort im linken Segment des Chips und über dem Inhalt der Karte. */
export const VERWEIS_MODULWORT: Record<VerweisArt, string> = {
  cmdb: "CMDB",
  workflow: "Workflow",
  person: "Person",
  wiki: "Wiki",
  intranet: "Intranet",
  aufgabe: "Projekte",
  prozess: "Prozess",
};

/** Der Name-Platz eines Verweises, den die Person nicht sehen darf (kein Name, kein Grund; gelöscht und kein Recht gleich). */
export const VERWEIS_NICHT_VERFUEGBAR_TEXT = "Nicht verfügbar";

/**
 * Marke, die der Server (`bereinigeVerweiseFuerLeser`) an die Stelle von Kennung und Adresse setzt, bevor der Inhalt an die
 * Ansicht geht. Sie ist keine Kennung eines Ziels: der Lader der Karte beantwortet sie ohne Zugriff mit dem festen Satz.
 */
export const VERWEIS_NICHT_VERFUEGBAR_MARKE = "nicht-verfuegbar";

/**
 * Marke des Chips einer Gattung (Grunddaten-Sicht, nur das Wort der Art, W191): der Chip hat im Client keine Kennung, setzt
 * selbst einen Anker mit dieser Marke, und die Karte beantwortet sie ohne Serveraufruf mit dem festen Satz
 * {@link VERWEIS_ART_HINWEIS}. Wie {@link VERWEIS_NICHT_VERFUEGBAR_MARKE} keine Kennung eines Ziels.
 */
export const VERWEIS_GRUNDSICHT_ART_MARKE = "grundsicht-art";

/** Fußzeile der Karte einer Grundsicht (Name und Grundangaben, ohne Detailseite). */
export const VERWEIS_GRUNDSICHT_HINWEIS = "Du siehst nur die Grundangaben. Mehr kannst du hier nicht öffnen.";

/** Satz der Karte einer Gattung bzw. einer Aufgabe Stufe A (nur die Art des Elements). */
export const VERWEIS_ART_HINWEIS = "Du siehst nur die Art des Elements. Mehr kannst du hier nicht öffnen.";

/** Antwort der Karte auf eine Marke (ohne Serveraufruf); gleiche Form wie der Inhalt der Hovercard. */
export type VerweisMarkenAntwort =
  | { kind: "ok"; title: string; hinweis: string }
  | { kind: "missing"; reason: string };

/**
 * Beantwortet die Marken, die der Server oder der Chip an die Stelle einer Kennung setzt, ohne Aufruf (es gibt kein Ziel,
 * nach dem gefragt werden könnte); jede andere Kennung ist `null` und geht an die gesicherte Action, die selbst prüft.
 */
export function antwortFuerVerweisMarke(id: string): VerweisMarkenAntwort | null {
  if (id === VERWEIS_NICHT_VERFUEGBAR_MARKE) return { kind: "missing", reason: VERWEIS_NICHT_VERFUEGBAR_TEXT };
  if (id === VERWEIS_GRUNDSICHT_ART_MARKE) return { kind: "ok", title: "", hinweis: VERWEIS_ART_HINWEIS };
  return null;
}

/**
 * Beginn der Platzhalter-Kennung, die der Server in der BEARBEITEN-Ansicht an die Stelle der Kennung eines nicht verfügbaren
 * Verweises setzt (Kennzeichen ohne Bedeutung außerhalb des Servers, Beschreibung: `verweis-schwaerzen.ts`, Betriebsart
 * BEARBEITEN). Die Ansicht zeigt jede Kennung mit diesem Beginn als „nicht verfügbar“ und ohne Hover-Anker.
 */
export const VERWEIS_PLATZHALTER_PRAEFIX = "nv~";

/** Ist dies die Kennung eines Platzhalters (nicht verfügbarer Verweis in der Bearbeiten-Ansicht)? */
export function istVerweisPlatzhalter(kennung: unknown): boolean {
  return typeof kennung === "string" && kennung.startsWith(VERWEIS_PLATZHALTER_PRAEFIX);
}

/** Adresse, die ein nicht verfügbarer Wiki-Verweis (Link-Marke) nach der Bereinigung trägt. */
export const VERWEIS_NICHT_VERFUEGBAR_HREF = "#nicht-verfuegbar";
